import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Center,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  CollectionPage,
  type Column,
  type Item,
  type Keyable,
  HelixLink,
  useCollection,
} from "@sciencecorp/helix-components";
import type { Facets } from "@sciencecorp/helix-components";
import {
  useAdminAccounts,
  APPLICATION_STATUS_FILTER_VALUES,
  type AdminAccount,
  type ApplicationStatusFilter,
} from "@/api/admin/accounts";
import { formatDate } from "@/utils/dateFormat";
import { localeLabels, supportedLocales, type SupportedLocale } from "@/i18n";
import { AdminHeader } from "./AdminHeader";
import { formatStatus, getCountryName, lifecycleOf } from "./utils";

const formatLocale = (locale: string): string =>
  localeLabels[locale as SupportedLocale] ?? locale;

type AccountItem = AdminAccount & Keyable;

// Status filter accepts the 5-bucket extension that includes "draft". The
// response status enum stays 4 values; "draft" is a filter-only dimension.
const STATUS_LABELS: Record<ApplicationStatusFilter, string> = {
  none: "No application",
  draft: "Draft (started, not submitted)",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const renderStatus = (value: unknown): string => {
  const key = String(value) as ApplicationStatusFilter;
  return STATUS_LABELS[key] ?? key;
};

const renderLanguage = (value: unknown): string => formatLocale(String(value));
const renderCountry = (value: unknown): string => getCountryName(String(value));

const TRAVEL_WILLING_OPTIONS = ["yes", "no"] as const;
const renderTravelWilling = (value: unknown): string => (value === "yes" ? "Yes" : "No");

const useDebounced = <T,>(value: T, delay = 250): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export const Accounts = () => {
  const { facets, pagination, order, onFacets, onPagination, onOrder } = useCollection({
    // 20 (not 25): helix's per-page select only has [10, 20, 50, 100] options.
    pagination: { per_page: 20 },
    order: { email: "asc" },
  });

  const [emailInput, setEmailInput] = useState("");
  const debouncedEmail = useDebounced(emailInput);
  const [nameInput, setNameInput] = useState("");
  const debouncedName = useDebounced(nameInput);

  const selectedStatuses = useMemo<ApplicationStatusFilter[]>(() => {
    const opts = facets.application_status?.options ?? [];
    return opts
      .filter((o) => o.selected)
      .map((o) => String(o.value) as ApplicationStatusFilter);
  }, [facets.application_status]);

  const selectedLocales = useMemo<string[]>(() => {
    const opts = facets.locale?.options ?? [];
    return opts.filter((o) => o.selected).map((o) => String(o.value));
  }, [facets.locale]);

  const selectedCountries = useMemo<string[]>(() => {
    const opts = facets.country?.options ?? [];
    return opts.filter((o) => o.selected).map((o) => String(o.value));
  }, [facets.country]);

  const selectedTravelWilling = useMemo<string[]>(() => {
    const opts = facets.travel_willing?.options ?? [];
    return opts.filter((o) => o.selected).map((o) => String(o.value));
  }, [facets.travel_willing]);

  const { data, isLoading, error } = useAdminAccounts({
    filters: {
      email: debouncedEmail || undefined,
      name: debouncedName || undefined,
      application_status: selectedStatuses.length ? selectedStatuses : undefined,
      locale: selectedLocales.length ? selectedLocales : undefined,
      country: selectedCountries.length ? selectedCountries : undefined,
      travel_willing: selectedTravelWilling.length ? selectedTravelWilling : undefined,
    },
    page: pagination.page,
    per_page: pagination.per_page,
    order: { email: order.email === "desc" ? "desc" : "asc" },
  });

  // useCollection doesn't reset pagination when facets change — applying a
  // filter while on page 5 lands the user beyond the new total. Reset to
  // page 1 alongside the facet update.
  const handleFacets = useCallback(
    (next: Facets) => {
      onFacets(next);
      onPagination({ ...pagination, page: 1 });
    },
    [onFacets, onPagination, pagination],
  );

  const accounts: AccountItem[] = useMemo(
    () => (data?.accounts ?? []).map((a) => ({ ...a, key: a.id })),
    [data?.accounts],
  );

  // Country options come from the server (distinct values present in the
  // data) plus any country currently selected in the URL — so a selected
  // country stays visible even when zero rows match the rest of the filters.
  // Sorted alphabetically by display name.
  const countryOptions = useMemo(() => {
    const fromServer = data?.country_options ?? [];
    const merged = Array.from(new Set([...fromServer, ...selectedCountries]));
    return merged.sort((a, b) => getCountryName(a).localeCompare(getCountryName(b)));
  }, [data?.country_options, selectedCountries]);

  // Build the facet definitions fresh each render. Counts are not returned
  // by the server for this page; keep them at 0 so the helix UI renders
  // checkboxes without a count column.
  const displayFacets = useMemo<Facets>(
    () => ({
      application_status: {
        label: "Application Status",
        render: renderStatus,
        options: APPLICATION_STATUS_FILTER_VALUES.map((v) => ({
          value: v,
          key: v,
          count: 0,
          selected: selectedStatuses.includes(v),
        })),
      },
      locale: {
        label: "Language",
        render: renderLanguage,
        options: supportedLocales.map((v) => ({
          value: v,
          key: v,
          count: 0,
          selected: selectedLocales.includes(v),
        })),
      },
      country: {
        label: "Country",
        render: renderCountry,
        options: countryOptions.map((v) => ({
          value: v,
          key: v,
          count: 0,
          selected: selectedCountries.includes(v),
        })),
      },
      travel_willing: {
        label: "Willing to Travel",
        render: renderTravelWilling,
        options: TRAVEL_WILLING_OPTIONS.map((v) => ({
          value: v,
          key: v,
          count: 0,
          selected: selectedTravelWilling.includes(v),
        })),
      },
    }),
    [selectedStatuses, selectedLocales, selectedCountries, countryOptions, selectedTravelWilling],
  );

  const columns: Column<AccountItem>[] = useMemo(
    () => [
      {
        label: "ID",
        render: (item: Item<AccountItem>) => (
          <HelixLink
            model="account"
            id={item.id}
            identifier={item.id.slice(0, 8)}
            disablePopover
            modelName="Account"
            size="small"
            navigateTo={`/admin/accounts/${item.id}`}
          />
        ),
      },
      {
        label: "Email",
        orderOptions: { orderKey: "email" },
        render: (item: Item<AccountItem>) => <Text>{item.email}</Text>,
      },
      {
        label: "Name",
        render: (item: Item<AccountItem>) => (
          <Text>{[item.first_name, item.last_name].filter(Boolean).join(" ") || "—"}</Text>
        ),
      },
      {
        label: "Language",
        render: (item: Item<AccountItem>) => <Text>{formatLocale(item.locale)}</Text>,
      },
      {
        label: "Roles",
        render: (item: Item<AccountItem>) => {
          const roles = [
            item.is_admin ? "Admin" : null,
            item.is_reviewer ? "Reviewer" : null,
          ].filter(Boolean);
          return <Text>{roles.length ? roles.join(", ") : "—"}</Text>;
        },
      },
      {
        label: "Application",
        render: (item: Item<AccountItem>) => {
          const lifecycle = lifecycleOf(item);
          switch (lifecycle.kind) {
            case "none":
              return <Text color="gray.500">No application</Text>;
            case "draft":
              return <Text color="orange.600">Draft</Text>;
            default: {
              const { text, color } = formatStatus(lifecycle.kind);
              return <Text color={color}>{text}</Text>;
            }
          }
        },
      },
      {
        label: "Last reviewed",
        render: (item: Item<AccountItem>) => (
          <Text>{item.last_review_at ? formatDate(item.last_review_at) : "—"}</Text>
        ),
      },
    ],
    [],
  );

  // First-load failure (no cached data to fall back to): full-page error.
  if (error && !data) {
    return (
      <Center py={20}>
        <Text color="red.500">
          Error loading accounts: {error instanceof Error ? error.message : "Unknown error"}
        </Text>
      </Center>
    );
  }

  if (isLoading && !data) {
    return (
      <Center py={20}>
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <AdminHeader stats={data?.stats} />
      {/* Refresh failure with stale data still visible. Banner above the
          table so the admin doesn't think a filter change took effect when
          it actually didn't. */}
      {Boolean(error) && (
        <Alert status="error" marginBottom={4}>
          <AlertIcon />
          <Box>
            <AlertTitle>Failed to refresh</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : "Unknown error"}. Showing the
              previous results.
            </AlertDescription>
          </Box>
        </Alert>
      )}
      <VStack align="stretch" spacing={4} marginBottom={4}>
        <HStack spacing={3}>
          <Box flex="1" maxWidth="400px">
            <Input
              placeholder="Search by email (starts with…)"
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                onPagination({ ...pagination, page: 1 });
              }}
              size="sm"
            />
          </Box>
          <Box flex="1" maxWidth="400px">
            <Input
              placeholder="Search by name (account, patient, or proxy)"
              value={nameInput}
              onChange={(e) => {
                setNameInput(e.target.value);
                onPagination({ ...pagination, page: 1 });
              }}
              size="sm"
            />
          </Box>
        </HStack>
      </VStack>
      <CollectionPage
        title="Accounts"
        columns={columns}
        isLoading={isLoading}
        items={accounts}
        facets={displayFacets}
        facetsProps={{ defaultIndex: [0, 1, 2] }}
        pagination={{
          page: data?.pagination.page ?? pagination.page,
          per_page: data?.pagination.per_page ?? pagination.per_page,
          n_pages: data?.pagination.n_pages ?? 0,
          total: data?.pagination.total ?? 0,
        }}
        order={order}
        onFacets={handleFacets}
        onPagination={onPagination}
        onOrder={onOrder}
      />
    </>
  );
};
